import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ClipboardCheck, HeartHandshake, ListChecks, RefreshCcw, Stethoscope, Users, Building2 } from "lucide-react";
import { buttonVariants, cn } from "@dosa/ui";

export function HomePage() {
  const { t } = useTranslation();

  const pillars = [
    { key: "reconciliation", icon: ListChecks },
    { key: "review", icon: ClipboardCheck },
    { key: "plan", icon: HeartHandshake },
    { key: "followup", icon: RefreshCcw },
  ] as const;

  return (
    <div>
      <section className="border-b border-charcoal-100 bg-gradient-to-b from-primary-50/60 to-ivory">
        <div className="container-app grid gap-10 py-16 lg:grid-cols-2 lg:items-center lg:py-24">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-primary-700">{t("home.heroEyebrow")}</p>
            <h1 className="mt-3 text-4xl font-semibold leading-tight text-charcoal-900 sm:text-5xl">{t("home.heroTitle")}</h1>
            <p className="mt-5 max-w-xl text-lg text-charcoal-600">{t("home.heroSubtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book-a-review" className={cn(buttonVariants({ size: "lg" }))}>
                {t("home.ctaPrimary")}
              </Link>
              <Link to="/how-it-works" className={cn(buttonVariants({ variant: "outline", size: "lg" }))}>
                {t("home.ctaSecondary")}
              </Link>
            </div>
            <p className="mt-6 text-sm text-charcoal-400">{t("home.trustStrip")}</p>
          </div>
          <div className="relative">
            <div className="rounded-xl border border-charcoal-100 bg-white p-6 shadow-elevated">
              <div className="mb-4 flex items-center gap-3 border-b border-charcoal-100 pb-4">
                <div className="rounded-full bg-primary-100 p-2 text-primary-700">
                  <Stethoscope className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-charcoal-800">{t("services.initialMtm.title")}</p>
                  <p className="text-xs text-charcoal-400">45 {t("common.time")}</p>
                </div>
              </div>
              <ul className="space-y-3 text-sm text-charcoal-600">
                {pillars.map((p) => (
                  <li key={p.key} className="flex items-start gap-2.5">
                    <p.icon className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" />
                    {t(`home.pillars.${p.key}.title`)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="absolute -end-4 -top-4 hidden rounded-lg border border-gold-400/30 bg-white px-3 py-2 text-xs font-medium text-gold-600 shadow-card sm:block">
              SCFHS-licensed pharmacists
            </div>
          </div>
        </div>
      </section>

      <section className="container-app py-16 lg:py-20">
        <h2 className="text-center text-3xl font-semibold text-charcoal-900">{t("home.pillarsTitle")}</h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p) => (
            <div key={p.key} className="rounded-lg border border-charcoal-100 bg-white p-6 shadow-card">
              <div className="mb-4 inline-flex rounded-md bg-primary-50 p-2.5 text-primary-700">
                <p.icon className="h-5 w-5" />
              </div>
              <h3 className="font-semibold text-charcoal-800">{t(`home.pillars.${p.key}.title`)}</h3>
              <p className="mt-1.5 text-sm text-charcoal-500">{t(`home.pillars.${p.key}.body`)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-charcoal-900 py-16 text-ivory">
        <div className="container-app">
          <h2 className="text-center text-2xl font-semibold">{t("home.audienceTitle")}</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {[
              { icon: Users, label: t("roles.patient") },
              { icon: Stethoscope, label: t("roles.pharmacist") },
              { icon: Building2, label: t("roles.admin") },
            ].map((a) => (
              <div key={a.label} className="flex flex-col items-center gap-3 rounded-lg border border-charcoal-700 p-6 text-center">
                <a.icon className="h-6 w-6 text-gold-400" />
                <p className="font-medium">{a.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-app py-16 text-center lg:py-20">
        <h2 className="text-3xl font-semibold text-charcoal-900">{t("home.finalCtaTitle")}</h2>
        <p className="mx-auto mt-3 max-w-xl text-charcoal-600">{t("home.finalCtaBody")}</p>
        <Link to="/book-a-review" className={cn(buttonVariants({ size: "lg" }), "mt-8 inline-flex")}>
          {t("home.ctaPrimary")}
        </Link>
      </section>
    </div>
  );
}
